import React, { useEffect, useMemo, useState } from 'react';
import { BRAND } from '../data/constants';
import { blurInput, focusInput, inputStyle } from '../utils/helpers';

export default function ModalPrompt({
  open,
  title,
  description,
  placeholder,
  defaultValue,
  confirmLabel,
  cancelLabel,
  multiline,
  onClose,
  onConfirm,
  disabled,
}) {
  const [value, setValue] = useState('');

  const isOpen = !!open;
  const confirmText = confirmLabel || 'Confirmer';
  const cancelText = cancelLabel || 'Annuler';

  const initial = useMemo(() => {
    return defaultValue === undefined || defaultValue === null ? '' : String(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (isOpen) setValue(initial);
  }, [initial, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (typeof onClose === 'function') onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const canConfirm = !disabled;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        backdropFilter: 'blur(4px)',
      }}
      onClick={(e) => e.target === e.currentTarget && typeof onClose === 'function' && onClose()}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 20,
          padding: 18,
          width: '100%',
          maxWidth: 480,
          boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
          animation: 'fadeIn 0.2s ease-out',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', marginBottom: 6 }}>
          {title || 'Saisie'}
        </div>

        {!!description && (
          <div style={{ fontSize: 13, color: '#64748b', fontWeight: 700, marginBottom: 12 }}>
            {description}
          </div>
        )}

        {multiline ? (
          <textarea
            rows={4}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder || ''}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 110 }}
            onFocus={focusInput}
            onBlur={blurInput}
          />
        ) : (
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder || ''}
            style={inputStyle}
            onFocus={focusInput}
            onBlur={blurInput}
            autoFocus
          />
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => typeof onClose === 'function' && onClose()}
            disabled={disabled}
            style={{
              padding: '12px 14px',
              borderRadius: 14,
              border: 'none',
              background: '#F1F5F9',
              color: '#334155',
              fontWeight: 900,
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              flex: 1,
              minWidth: 140,
            }}
          >
            {cancelText}
          </button>

          <button
            type="button"
            onClick={() => typeof onConfirm === 'function' && onConfirm(value)}
            disabled={!canConfirm}
            style={{
              padding: '12px 14px',
              borderRadius: 14,
              border: 'none',
              background: !canConfirm ? '#e2e8f0' : BRAND.gradient,
              color: 'white',
              fontWeight: 900,
              cursor: !canConfirm ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              flex: 1,
              minWidth: 140,
              boxShadow: !canConfirm ? 'none' : '0 4px 16px rgba(79,70,229,0.25)',
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
